import { useEffect, useMemo, useState } from "react";
import { Avatar, Button, List, Card, Tag, Space, Divider, Modal, message, Table, Input, Alert, Typography } from "antd";
import { PlusOutlined, HistoryOutlined, ArrowUpOutlined, ArrowDownOutlined, CheckCircleOutlined, CloseCircleOutlined, EditOutlined } from "@ant-design/icons";
import { uniqueBy, libelleGarantie, normaliserGaranties } from "../../../../../variables/stringFunctions";
import { validateEvaluation, rejectEvaluation } from "../../../../../hooks/useHooks";
import axiosInstance from "../../../../../variables/api/axiosInstance";
import ReevaluationGarantieModal from "../../../components/modals/ReevaluationGarantieModal";

const { Text } = Typography;

// État de validation d'une évaluation (enum backend EtatEvaluation) → libellé + couleur.
const ETAT_META = {
    EN_ATTENTE: { label: "À valider", color: "gold" },
    VALIDE: { label: "Validé", color: "green" },
    REJETE: { label: "Rejeté", color: "red" },
    ANNULE: { label: "Annulé", color: "default" },
};

const frenchDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit"
    });
};

const SapTab = ({ sinistre, evaluations, garantiesContrat = [], onReevaluationClick, onChanged }) => {
    const readOnly = !!sinistre?.lectureSeule;
    // Suivi par garantie (Phase 1) : provision courante − règlements ventilés, par garantie.
    const [sapGaranties, setSapGaranties] = useState([]);
    const [reevalGarantie, setReevalGarantie] = useState(null);
    const [reevalOpen, setReevalOpen] = useState(false);

    // Garanties concernées (celles cochées à la déclaration = `sinistre.garanties`). Éditables ici :
    // elles servent de référentiel pour restreindre la réévaluation.
    const [garantiesOpen, setGarantiesOpen] = useState(false);
    const [garantiesSelection, setGarantiesSelection] = useState([]);
    const [savingGaranties, setSavingGaranties] = useState(false);
    // Les SURPRIMES ne sont JAMAIS une garantie concernée à part : elles font partie de la RC.
    // On ne les propose donc jamais dans la liste (ni par défaut).
    const estSurprime = (n) => (n || "").toString().toLowerCase().includes("surprime");
    // Noms de garantie du CONTRAT (canoniques), normalisés (variantes RC → « RC ») + dédupliqués.
    const nomsContrat = useMemo(
        () => normaliserGaranties((garantiesContrat || []).map((g) => g?.label || g?.garantie?.nom))
            .filter((n) => !estSurprime(n)),
        [garantiesContrat]
    );
    // Cases proposées = garanties du contrat ∪ garanties déjà déclarées, normalisées (« RC de base »
    // et « RC » ne forment qu'une seule case « RC »), SANS les surprimes.
    const garantiesDisponibles = useMemo(
        () => normaliserGaranties([...nomsContrat, ...((sinistre?.garanties) || [])])
            .filter((n) => !estSurprime(n))
            .sort((a, b) => a.localeCompare(b)),
        [nomsContrat, sinistre?.garanties]
    );
    const ouvrirGaranties = () => {
        if (readOnly) { message.warning("Dossier en lecture seule : action impossible."); return; }
        // Défaut : garanties déclarées (normalisées) si présentes ; sinon (anciens dossiers non
        // renseignés) TOUTES les garanties du contrat cochées. Surprimes exclues dans tous les cas.
        const declarees = normaliserGaranties(sinistre?.garanties).filter((n) => !estSurprime(n));
        setGarantiesSelection(declarees.length ? declarees : [...nomsContrat]);
        setGarantiesOpen(true);
    };
    const enregistrerGaranties = () => {
        if (readOnly) { message.warning("Dossier en lecture seule : action impossible."); return; }
        setSavingGaranties(true);
        axiosInstance.put(`/api/sinistre/${sinistre.codeId}/garanties`, garantiesSelection.filter((n) => !estSurprime(n)))
            .then(() => { message.success("Garanties concernées mises à jour."); setGarantiesOpen(false); onChanged && onChanged(); })
            .catch((e) => message.error(e?.response?.data?.message || "Échec de la mise à jour."))
            .finally(() => setSavingGaranties(false));
    };
    const toggleGarantie = (nom, checked) => {
        setGarantiesSelection((prev) => checked ? [...new Set([...prev, nom])] : prev.filter((x) => x !== nom));
    };
    const chargerSapGaranties = () => {
        if (!sinistre?.codeId) return;
        axiosInstance.get(`/api/sinistre/sap-garanties/${sinistre.codeId}`)
            .then((r) => setSapGaranties(r.data || []))
            .catch(() => setSapGaranties([]));
    };
    useEffect(() => { chargerSapGaranties(); /* eslint-disable-next-line */ }, [sinistre?.codeId, evaluations]);

    // Solder / désolder une garantie (Phase 3).
    const solderGarantie = (r) => {
        if (readOnly) { message.warning("Dossier en lecture seule : action impossible."); return; }
        let motif = "";
        Modal.confirm({
            title: `Solder la garantie « ${r.garantieNom || r.garantieCodeId} » ?`,
            content: (
                <div>
                    <p>Son SAP ({formatAmount(r.sap)} FCFA) sera figé et la garantie considérée close.</p>
                    <Input.TextArea rows={2} placeholder="Motif (facultatif)" onChange={(e) => { motif = e.target.value; }} />
                </div>
            ),
            okText: "Solder", cancelText: "Annuler",
            onOk: () => axiosInstance.post(`/api/sinistre/solder-garantie/${sinistre.codeId}/${r.garantieCodeId}`, { motif })
                .then(() => { message.success("Garantie soldée."); chargerSapGaranties(); })
                .catch((e) => message.error(e?.response?.data?.message || "Échec.")),
        });
    };
    const desolderGarantie = (r) => {
        if (readOnly) { message.warning("Dossier en lecture seule : action impossible."); return; }
        return axiosInstance.delete(`/api/sinistre/solder-garantie/${sinistre.codeId}/${r.garantieCodeId}`)
        .then(() => { message.success("Solde de la garantie annulé."); chargerSapGaranties(); })
        .catch((e) => message.error(e?.response?.data?.message || "Échec."));
    };

    const toutesSoldees = sapGaranties.length > 0 && sapGaranties.every((g) => g.soldee);

    // Historique d'UNE garantie (versionnement par garantie) : sa provision au fil des évaluations.
    const historiqueGarantie = (garantieCodeId, garantieNom) => {
        const rows = [];
        (evaluations || [])
            .filter((e) => e && e.activeData !== false && e.recours !== true)
            .sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt))
            .forEach((e) => {
                const se = (e.sousEvaluations || []).find((x) =>
                    (x?.garantieAffecte?.codeId && x.garantieAffecte.codeId === garantieCodeId)
                    || (x?.garantieAffectee && x.garantieAffectee === garantieNom));
                if (se) {
                    rows.push({
                        codeId: e.codeId,
                        date: e.createdAt,
                        montant: se.montantTotal,
                        etat: e.etatEvaluation,
                        revisee: e.garantieReviseeCodeId === garantieCodeId,
                        motif: e.motifRevision,
                    });
                }
            });
        return rows;
    };

    // Découpage principal/honoraires COURANT d'une garantie (sous-évaluation de l'évaluation la plus
    // récente), pour pré-remplir correctement la réévaluation d'une garantie.
    const garantieSplitCourant = (garantieCodeId, garantieNom) => {
        const eval0 = (evaluations || [])
            .filter((e) => e && e.activeData !== false && e.recours !== true)
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0];
        const se = eval0 && (eval0.sousEvaluations || []).find((x) =>
            (x?.garantieAffecte?.codeId && x.garantieAffecte.codeId === garantieCodeId)
            || (x?.garantieAffectee && x.garantieAffectee === garantieNom));
        return {
            principal: se?.montantPrincipal ?? null,
            honoraires: se?.montantHonoraires ?? null,
        };
    };

    const sinistreEvaluations = useMemo(() => {
        const filtered = evaluations?.filter((evaluation) => evaluation.encaissement !== true) || [];
        // Sort from newest to oldest (most recent first)
        return uniqueBy(filtered.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)), "codeId");
    }, [evaluations]);

    // Validation / rejet d'une réévaluation SUR PLACE (même action que /validation-evaluation, sans
    // changer de page). Mêmes endpoints (validateEvaluation/rejectEvaluation) et même règle d'état :
    // EN_ATTENTE → Valider + Rejeter ; VALIDE → Rejeter. onChanged() recharge le dossier (SAP, courante).
    const doValidate = (item) => {
        if (readOnly) { message.warning("Dossier en lecture seule : action impossible."); return; }
        Modal.confirm({
            title: "Valider cette réévaluation ?",
            icon: <CheckCircleOutlined style={{ color: "#16a34a" }} />,
            content: `Nouvelle évaluation : ${formatAmount(item?.nouvelleProvision)} FCFA`,
            okText: "Valider", cancelText: "Annuler",
            okButtonProps: { style: { background: "#16a34a", borderColor: "#16a34a" } },
            onOk: () => validateEvaluation(item.codeId)
                .then(() => { message.success("Réévaluation validée"); onChanged && onChanged(); })
                .catch(() => message.error("Échec de la validation")),
        });
    };
    const doReject = (item) => {
        if (readOnly) { message.warning("Dossier en lecture seule : action impossible."); return; }
        Modal.confirm({
            title: "Rejeter cette réévaluation ?",
            icon: <CloseCircleOutlined style={{ color: "#dc2626" }} />,
            content: "L'évaluation reviendra à la version précédente.",
            okText: "Rejeter", okType: "danger", cancelText: "Annuler",
            onOk: () => rejectEvaluation(item.codeId)
                .then(() => { message.success("Réévaluation rejetée"); onChanged && onChanged(); })
                .catch(() => message.error("Échec du rejet")),
        });
    };

    const isFirstEvaluation = (index) => {
        // First evaluation is the oldest one, which is at the end of the list
        return index === sinistreEvaluations.length - 1;
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat("fr-FR").format(Math.trunc(amount || 0));
    };

    const getDifference = (nouvelle, ancienne) => {
        const diff = nouvelle - ancienne;
        return {
            value: Math.abs(diff),
            isIncrease: diff > 0,
            percentage: ancienne ? ((diff / ancienne) * 100).toFixed(1) : 0
        };
    };

    return (
        <div style={{ padding: "24px", backgroundColor: "#f5f7fa", minHeight: "100%" }}>
            {/* Header Section */}
            <div style={{
                marginBottom: "24px",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "16px"
            }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: "24px", fontWeight: 600, color: "#1a1a1a" }}>
                        <HistoryOutlined style={{ marginRight: "12px", color: '#242166' }} />
                        Historique des Réévaluations
                    </h2>
                    <p style={{ margin: "4px 0 0 0", color: "#6b7280", fontSize: "14px" }}>
                        {sinistreEvaluations.length} réévaluation(s) enregistrée(s)
                    </p>
                    <div style={{ marginTop: 8, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6 }}>
                        <span style={{ fontSize: 12, color: "#6b7280", fontWeight: 500 }}>Garanties concernées :</span>
                        {normaliserGaranties(sinistre?.garanties).length > 0
                            ? normaliserGaranties(sinistre?.garanties).map((g) => <Tag key={g} color="blue">{g}</Tag>)
                            : <Tag color="orange">Aucune définie</Tag>}
                    </div>
                </div>
                <Space wrap>
                    <Button
                        icon={<EditOutlined />}
                        size="large"
                        onClick={ouvrirGaranties}
                        disabled={readOnly}
                        style={{ height: "44px", borderRadius: "8px", fontWeight: 500 }}
                    >
                        Modifier les garanties concernées
                    </Button>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        size="large"
                        onClick={onReevaluationClick}
                        disabled={sinistre?.sortDossier === "T" || sinistre?.sortDossier === "S" || readOnly}
                        style={{
                            backgroundColor: '#242166',
                            borderColor: "#000",
                            height: "44px",
                            borderRadius: "8px",
                            fontWeight: 500,
                            boxShadow: "0 2px 8px rgba(79, 70, 229, 0.3)"
                        }}
                    >
                        Nouvelle Réévaluation
                    </Button>
                </Space>
            </div>

            {/* SAP par garantie (suivi par garantie) */}
            {sapGaranties.length > 0 && (
                <Card style={{ marginBottom: "16px", borderRadius: "12px", border: "1px solid #e5e7eb" }} bodyStyle={{ padding: "12px 16px" }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: "#1f2937" }}>SAP par garantie</div>
                    <Table rowKey={(r) => r.garantieCodeId || r.garantieNom} size="small" pagination={false} dataSource={sapGaranties}
                        expandable={{
                            expandedRowRender: (r) => {
                                const hist = historiqueGarantie(r.garantieCodeId, r.garantieNom);
                                return hist.length ? (
                                    <Table rowKey="codeId" size="small" pagination={false} dataSource={hist}
                                        columns={[
                                            { title: "Date", dataIndex: "date", render: (v) => v ? new Date(v).toLocaleDateString("fr-FR") : "—" },
                                            { title: "Provision", dataIndex: "montant", align: "right", render: (v) => `${formatAmount(v)} FCFA` },
                                            { title: "État", dataIndex: "etat", render: (v) => <Tag>{v}</Tag> },
                                            { title: "", dataIndex: "revisee", render: (v, row) => v ? <Tag color="purple">Garantie révisée{row.motif ? ` · ${row.motif}` : ""}</Tag> : null },
                                        ]} />
                                ) : <Text type="secondary">Aucun historique</Text>;
                            },
                        }}
                        columns={[
                            { title: "Garantie", dataIndex: "garantieNom", render: (v, r) => libelleGarantie(v) || r.garantieCodeId || "—" },
                            { title: "Provision", dataIndex: "provision", align: "right", render: (v) => `${formatAmount(v)} FCFA` },
                            { title: "Réglé", dataIndex: "regle", align: "right", render: (v) => `${formatAmount(v)} FCFA` },
                            {
                                title: "SAP", dataIndex: "sap", align: "right",
                                render: (v, r) => r.soldee
                                    ? <Tag color="green">Soldée</Tag>
                                    : <b style={{ color: (Number(v) || 0) > 0 ? "#b91c1c" : "#16a34a" }}>{formatAmount(v)} FCFA</b>,
                            },
                            {
                                title: "", key: "act", align: "right", width: 230,
                                render: (_, r) => r.soldee ? (
                                    <Space>
                                        <span style={{ fontSize: 11, color: "#6b7280" }}>Soldée le {r.soldeeLe ? new Date(r.soldeeLe).toLocaleDateString("fr-FR") : "—"}</span>
                                        <Button size="small" disabled={readOnly} onClick={() => desolderGarantie(r)}>Désolder</Button>
                                    </Space>
                                ) : (
                                    <Space>
                                        <Button size="small" disabled={readOnly} onClick={() => { setReevalGarantie({ ...r, ...garantieSplitCourant(r.garantieCodeId, r.garantieNom) }); setReevalOpen(true); }}>Réévaluer</Button>
                                        <Button size="small" type={(Number(r.sap) || 0) === 0 ? "primary" : "default"}
                                            disabled={readOnly}
                                            style={(Number(r.sap) || 0) === 0 ? { background: "#16a34a", borderColor: "#16a34a" } : {}}
                                            onClick={() => solderGarantie(r)}>Solder</Button>
                                    </Space>
                                ),
                            },
                        ]}
                        summary={(rows) => {
                            const p = rows.reduce((a, r) => a + (Number(r.provision) || 0), 0);
                            const rg = rows.reduce((a, r) => a + (Number(r.regle) || 0), 0);
                            const sp = rows.reduce((a, r) => a + (Number(r.sap) || 0), 0);
                            return (
                                <Table.Summary.Row style={{ background: "#fafafa", fontWeight: 700 }}>
                                    <Table.Summary.Cell index={0}>Total</Table.Summary.Cell>
                                    <Table.Summary.Cell index={1} align="right">{formatAmount(p)} FCFA</Table.Summary.Cell>
                                    <Table.Summary.Cell index={2} align="right">{formatAmount(rg)} FCFA</Table.Summary.Cell>
                                    <Table.Summary.Cell index={3} align="right">{formatAmount(sp)} FCFA</Table.Summary.Cell>
                                </Table.Summary.Row>
                            );
                        }}
                    />
                    {toutesSoldees && (
                        <Alert type="success" showIcon style={{ marginTop: 10 }}
                            message="Toutes les garanties sont soldées — le dossier peut être clôturé." />
                    )}
                </Card>
            )}

            <ReevaluationGarantieModal
                open={reevalOpen}
                onClose={() => setReevalOpen(false)}
                onSaved={() => { onChanged && onChanged(); }}
                sinistreCodeId={sinistre?.codeId}
                garantie={reevalGarantie}
            />

            {/* Modifier les garanties concernées (référentiel de la réévaluation) */}
            <Modal
                open={garantiesOpen}
                onCancel={() => setGarantiesOpen(false)}
                onOk={enregistrerGaranties}
                confirmLoading={savingGaranties}
                okText="Enregistrer"
                cancelText="Annuler"
                title="Garanties concernées par le sinistre"
                width={520}
            >
                <p style={{ color: "#6b7280", fontSize: 13, marginTop: 0 }}>
                    Cochez les garanties réellement concernées par ce sinistre. Seules ces garanties seront
                    proposées à la réévaluation.
                </p>
                {garantiesDisponibles.length === 0 ? (
                    <Alert type="warning" showIcon message="Aucune garantie n'a pu être chargée depuis le contrat." />
                ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 320, overflowY: "auto" }}>
                        {garantiesDisponibles.map((nom) => (
                            <label key={nom} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                                <input type="checkbox"
                                    checked={garantiesSelection.includes(nom)}
                                    onChange={(e) => toggleGarantie(nom, e.target.checked)} />
                                <span>{nom}</span>
                            </label>
                        ))}
                    </div>
                )}
                <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
                    {garantiesSelection.length} garantie(s) sélectionnée(s).
                </div>
            </Modal>

            {/* Content Section */}
            {sinistreEvaluations.length ? (
                <List
                    dataSource={sinistreEvaluations}
                    renderItem={(item, index) => {
                        const diff = getDifference(item?.nouvelleProvision, item?.ancienneProvision);
                        const isLatest = index === 0; // Most recent is first
                        const isFirst = isFirstEvaluation(index);

                        return (
                            <Card
                                key={index}
                                style={{
                                    marginBottom: "16px",
                                    borderRadius: "12px",
                                    border: isFirst
                                        ? "2px solid #10b981"
                                        : isLatest
                                            ? "2px solid #4f46e5"
                                            : "1px solid #e5e7eb",
                                    boxShadow: isFirst
                                        ? "0 4px 20px rgba(16, 185, 129, 0.15)"
                                        : isLatest
                                            ? "0 4px 20px rgba(79, 70, 229, 0.15)"
                                            : "0 2px 8px rgba(0, 0, 0, 0.06)",
                                    overflow: "hidden",
                                    transition: "all 0.3s ease",
                                    background: isFirst ? "linear-gradient(135deg, #f0fdf4 0%, #ffffff 100%)" : "#ffffff"
                                }}
                                hoverable
                            >
                                <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
                                    {/* Avatar Section */}
                                    <Avatar
                                        size={64}
                                        src={`https://ui-avatars.com/api/?name=${isFirst ? "Ouverture" : item?.motifRevision || "R"}&background=${isFirst ? "10b981" : "4f46e5"}&color=fff&bold=true&size=128`}
                                        style={{ flexShrink: 0, border: isFirst ? "3px solid #d1fae5" : "3px solid #f3f4f6" }}
                                    />

                                    {/* Content Section */}
                                    <div style={{ flex: 1 }}>
                                        {/* Header with Tags */}
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            marginBottom: "12px",
                                            flexWrap: "wrap",
                                            gap: "8px"
                                        }}>
                                            <div>
                                                <h3 style={{
                                                    margin: 0,
                                                    fontSize: "18px",
                                                    fontWeight: 600,
                                                    color: "#1a1a1a"
                                                }}>
                                                    {isFirst ? "Évaluation d'ouverture" : item?.motifRevision || "Réévaluation"}
                                                </h3>
                                                <p style={{
                                                    margin: "4px 0 0 0",
                                                    color: "#6b7280",
                                                    fontSize: "13px"
                                                }}>
                                                    {frenchDate(item?.createdAt)}
                                                </p>
                                            </div>
                                            <Space>
                                                {isFirst ? (
                                                    <Tag color="green" style={{
                                                        margin: 0,
                                                        padding: "4px 12px",
                                                        fontSize: "12px",
                                                        fontWeight: 500,
                                                        borderRadius: "6px"
                                                    }}>
                                                        Ouverture
                                                    </Tag>
                                                ) : isLatest && (
                                                    <Tag color="purple" style={{
                                                        margin: 0,
                                                        padding: "4px 12px",
                                                        fontSize: "12px",
                                                        fontWeight: 500,
                                                        borderRadius: "6px"
                                                    }}>
                                                        Courant
                                                    </Tag>
                                                )}
                                                {!isFirst && (
                                                    <Tag
                                                        color={diff.isIncrease ? "red" : "green"}
                                                        icon={diff.isIncrease ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                                                        style={{
                                                            margin: 0,
                                                            padding: "4px 12px",
                                                            fontSize: "12px",
                                                            fontWeight: 500,
                                                            borderRadius: "6px"
                                                        }}
                                                    >
                                                        {diff.isIncrease ? "+" : ""}{diff.percentage}%
                                                    </Tag>
                                                )}
                                                {/* Statut de validation */}
                                                {item?.etatEvaluation && ETAT_META[item.etatEvaluation] && (
                                                    <Tag color={ETAT_META[item.etatEvaluation].color} style={{ margin: 0, padding: "4px 12px", fontSize: "12px", fontWeight: 500, borderRadius: "6px" }}>
                                                        {ETAT_META[item.etatEvaluation].label}
                                                    </Tag>
                                                )}
                                                {/* Validation SUR PLACE (remplace /validation-evaluation) : uniquement sur la
                                                    DERNIÈRE réévaluation à l'état « À valider » (isLatest + EN_ATTENTE). */}
                                                {onChanged && isLatest && item?.etatEvaluation === "EN_ATTENTE" && (
                                                    <>
                                                        <Button size="small" type="primary" icon={<CheckCircleOutlined />}
                                                            disabled={readOnly}
                                                            style={{ background: "#16a34a", borderColor: "#16a34a" }}
                                                            onClick={() => doValidate(item)}>Valider</Button>
                                                        <Button size="small" danger icon={<CloseCircleOutlined />}
                                                            disabled={readOnly}
                                                            onClick={() => doReject(item)}>Rejeter</Button>
                                                    </>
                                                )}
                                            </Space>
                                        </div>

                                        <Divider style={{ margin: "12px 0" }} />

                                        {/* Provision Details */}
                                        <div style={{
                                            display: "grid",
                                            gridTemplateColumns: isFirst ? "1fr 1fr" : "repeat(auto-fit, minmax(250px, 1fr))",
                                            gap: "16px"
                                        }}>
                                            {/* Ancienne Provision - Hidden for first evaluation */}
                                            {!isFirst && (
                                                <div style={{
                                                    padding: "16px",
                                                    backgroundColor: "#fef3f2",
                                                    borderRadius: "8px",
                                                    border: "1px solid #fee2e2"
                                                }}>
                                                    <p style={{
                                                        margin: "0 0 4px 0",
                                                        fontSize: "12px",
                                                        color: "#991b1b",
                                                        fontWeight: 500,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px"
                                                    }}>
                                                        Ancienne Évaluation
                                                    </p>
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: "22px",
                                                        fontWeight: 700,
                                                        color: "black"
                                                    }}>
                                                        {formatAmount(item?.ancienneProvision)} <span style={{ fontSize: "14px", fontWeight: 500 }}>FCFA</span>
                                                    </p>
                                                </div>
                                            )}

                                            {/* Nouvelle Provision */}
                                            <div style={{
                                                padding: "16px",
                                                backgroundColor: isFirst ? "#f0fdf4" : diff.isIncrease ? "#fef2f2" : "#f0fdf4",
                                                borderRadius: "8px",
                                                border: isFirst ? "1px solid #bbf7d0" : diff.isIncrease ? "1px solid #fee2e2" : "1px solid #bbf7d0"
                                            }}>
                                                <p style={{
                                                    margin: "0 0 4px 0",
                                                    fontSize: "12px",
                                                    color: isFirst ? "#166534" : diff.isIncrease ? "#991b1b" : "#166534",
                                                    fontWeight: 500,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.5px"
                                                }}>
                                                    {isFirst ? "Evaluation Initiale" : isLatest ? "Evaluation Courante" : "Nouvelle Provision"}
                                                </p>
                                                <p style={{
                                                    margin: 0,
                                                    fontSize: "22px",
                                                    fontWeight: 700,
                                                    color: isFirst ? "black" : diff.isIncrease ? "black" : "black"
                                                }}>
                                                    {formatAmount(item?.nouvelleProvision)} <span style={{ fontSize: "14px", fontWeight: 500 }}>FCFA</span>
                                                </p>
                                            </div>

                                            {/* Date d'ouverture pour première évaluation */}
                                            {isFirst && (
                                                <div style={{
                                                    padding: "16px",
                                                    backgroundColor: "#eff6ff",
                                                    borderRadius: "8px",
                                                    border: "1px solid #dbeafe"
                                                }}>
                                                    <p style={{
                                                        margin: "0 0 4px 0",
                                                        fontSize: "12px",
                                                        color: "#1e40af",
                                                        fontWeight: 500,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px"
                                                    }}>
                                                        Date d'Ouverture
                                                    </p>
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: "16px",
                                                        fontWeight: 600,
                                                        color: "#2563eb"
                                                    }}>
                                                        {frenchDate(item?.createdAt)}
                                                    </p>
                                                </div>
                                            )}

                                            {/* Différence - Hidden for first evaluation */}
                                            {!isFirst && (
                                                <div style={{
                                                    padding: "16px",
                                                    backgroundColor: "#f8fafc",
                                                    borderRadius: "8px",
                                                    border: "1px solid #e2e8f0"
                                                }}>
                                                    <p style={{
                                                        margin: "0 0 4px 0",
                                                        fontSize: "12px",
                                                        color: "#475569",
                                                        fontWeight: 500,
                                                        textTransform: "uppercase",
                                                        letterSpacing: "0.5px"
                                                    }}>
                                                        Différence
                                                    </p>
                                                    <p style={{
                                                        margin: 0,
                                                        fontSize: "22px",
                                                        fontWeight: 700,
                                                        color: diff.isIncrease ? "#dc2626" : "#16a34a"
                                                    }}>
                                                        {diff.isIncrease ? "+" : "-"}{formatAmount(diff.value)} <span style={{ fontSize: "14px", fontWeight: 500 }}>FCFA</span>
                                                    </p>
                                                </div>
                                            )}
                                        </div>

                                        {/* Motif + observation de la réévaluation (et motif de rejet si REJETÉ) */}
                                        {!isFirst && (item?.motifRevision || item?.motifRevisionDetaille || (item?.etatEvaluation === "REJETE" && item?.motifRejet)) && (
                                            <div style={{ marginTop: "12px", padding: "12px 16px", background: "#f9fafb", borderRadius: "8px", border: "1px solid #e5e7eb" }}>
                                                {item?.motifRevision && (
                                                    <>
                                                        <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>Motif de la réévaluation</p>
                                                        <p style={{ margin: 0, fontSize: "14px", color: "#374151", whiteSpace: "pre-wrap" }}>{item.motifRevision}</p>
                                                    </>
                                                )}
                                                {item?.motifRevisionDetaille && (
                                                    <div style={{ marginTop: item?.motifRevision ? "8px" : 0 }}>
                                                        <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#6b7280", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>Observation</p>
                                                        <p style={{ margin: 0, fontSize: "14px", color: "#374151", whiteSpace: "pre-wrap" }}>{item.motifRevisionDetaille}</p>
                                                    </div>
                                                )}
                                                {item?.etatEvaluation === "REJETE" && item?.motifRejet && (
                                                    <div style={{ marginTop: (item?.motifRevision || item?.motifRevisionDetaille) ? "8px" : 0 }}>
                                                        <p style={{ margin: "0 0 4px 0", fontSize: "12px", color: "#991b1b", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.5px" }}>Motif de rejet</p>
                                                        <p style={{ margin: 0, fontSize: "14px", color: "#7f1d1d", whiteSpace: "pre-wrap" }}>{item.motifRejet}</p>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </Card>
                        );
                    }}
                />
            ) : (
                <Card style={{
                    borderRadius: "12px",
                    textAlign: "center",
                    padding: "40px 20px",
                    border: "2px dashed #d1d5db"
                }}>
                    <HistoryOutlined style={{ fontSize: "64px", color: "#d1d5db", marginBottom: "16px" }} />
                    <h3 style={{ fontSize: "18px", fontWeight: 600, color: "#4b5563", margin: "0 0 8px 0" }}>
                        Aucune réévaluation effectuée
                    </h3>
                    <p style={{ color: "#6b7280", margin: 0 }}>
                        Ce dossier sinistre n'a pas encore été réévalué
                    </p>
                </Card>
            )}
        </div>
    );
};

export default SapTab;